<!--
 * @Descripttion: 通用表格组件
 * @version:  1.0
 * @Author: kun.zhang
 * @Date: 2021-08-25 15:57:21
 * @LastEditors: xiaona.wang
 * @LastEditTime: 2022-01-10 17:28:16
-->
<template>
  <div ref="general_div" class="general_table_wrap">
    <!-- table header -->
    <header ref="table_header" class="table-header">
      <!-- 备用插槽 -->
      <slot name="tableSearchHeader"></slot>
      <slot name="tableSearchContent">
        <!-- Table search form -->
        <search-form
          :mode="searchMode"
          :form-options="formOptions"
          :basic="searchBasic"
          :advanced="searchAdvanced"
          :search-placeholder="searchPlaceholder"
          :title="title"
          v-on="$listeners"
          @search="handleSearch"
          @status="calculateTableHeight"
        >
          <template #operations>
            <slot name="operations">
              <!-- 没有操作默认行为 -->
              <el-button
                v-if="exportType === 'current'"
                type="primary"
                @click="exportType === 'current' ? exportCurrentData : exportAllData"
              >
                {{ exportType === "current" ? "导出当前页面" : "导出所有数据" }}
              </el-button>
            </slot>
          </template>
        </search-form>
      </slot>
      <!-- 备用插槽 -->
      <slot name="tableSearchFooter"></slot>
    </header>

    <!-- 批量删除 -->
    <batch-delete
      v-if="showBatchOperation"
      :page-size="pagination.pageSize"
      :total="pagination.total"
      :show-total="checkbox"
      :show-all-current="showAllCurrent || batchDelete"
      :show-all-total="false"
      :show-batch-delete="batchDelete"
      :current-select-num="multipleSelection.length"
      v-on="$listeners"
      @resetData="handleEmptySelection"
      @currentPageall="handleCheckCurrentPage"
      @deleteData="handleBatchDeleteData"
    >
      <template #batchL>
        <slot name="batchDeleteBatchL"></slot>
      </template>
      <template #batchR>
        <slot name="batchDeleteBatchR"></slot>
      </template>
    </batch-delete>

    <!-- TABLE CONTENT -->
    <el-table
      v-bind="$attrs"
      ref="generalTable"
      v-skeleton="false"
      class="general_table rc_table"
      :row-class-name="tableRowClassName"
      :height="tableHeight"
      :row-key="rowKey"
      :data="tableData"
      :size="tableSize"
      :class="[`rc_table_density--${tableDensity}`]"
      border
      v-on="$listeners"
      @selection-change="handleSelectionChange"
      @filter-change="handleChangeFilter"
    >
      <!-- 多选列 -->
      <el-table-column
        v-if="checkbox || batchDelete"
        type="selection"
        min-width="38"
        :reserve-selection="reserveSelection"
        fixed
      />
      <!-- 序号列 -->
      <el-table-column
        v-if="order"
        type="index"
        :label="$t('common.order')"
        min-width="80"
        :index="tableIndex"
        fixed
      />
      <!-- 内容列 -->
      <el-table-column
        v-for="(item, index) in viewColumns"
        v-bind="item"
        :key="index"
        :column-key="item.key"
        :prop="item.key || `${index}}`"
        :filter-method="item.serverFilter ? null : item.filters ?  filterHandler : null"
        :show-overflow-tooltip="contentTooltip"
      >
        <template v-if="item.filters" slot="header">
          {{ item.label }}
          <svg-icon icon-class="filter2" class="cursor-p" />
        </template>
        <template v-else slot="header">
          <slot :name="`header${$upperFirst(item.key)}`">
            {{ item.label }}
          </slot>
        </template>
        <template slot-scope="scope">
          <div class="ellipsisWarp">
            <slot :name="item.key" :item="scope.row">
              <span>{{ scope.row[item.key] }}</span>
            </slot>
          </div>
        </template>
      </el-table-column>
      <!-- 操作列 -->
      <el-table-column v-if="operable" fixed="right" :width="operableWidth">
        <template slot="header" slot-scope="scope">
          <span>{{ $t("public.operation") }}</span>
          <span v-if="false" class="cursor-p self-popover" title="刷新" @click="handleRefresh">
            <svg-icon icon-class="sync" />
          </span>
          <el-popover
            trigger="click"
            :visible-arrow="false"
            :visible.sync="densityVisible"
            popper-class="scim-common-theme scim-common-theme-green"
            class="cursor-p"
          >
            <span slot="reference" title="行高">
              <svg-icon icon-class="density" />
            </span>
            <div class="lineHeight_popover overflow-x-y">
              <ul>
                <li
                  v-for="density in densityOptions"
                  :key="density.value"
                  :class="{ active: density.value === tableDensity }"
                  @click="densityChange(density)"
                >
                  {{ density.label }}
                </li>
              </ul>
            </div>
          </el-popover>
          <el-popover
            trigger="click"
            :visible-arrow="false"
            popper-class="scim-common-theme"
            class="cursor-p self-popover"
          >
            <span slot="reference" title="字体大小">
              <svg-icon icon-class="size" />
            </span>
            <div class="fontsize_popover overflow-x-y">
              <ul>
                <li
                  v-for="fontItem in fontSizeOptions"
                  :key="fontItem.value"
                  :class="{ active: fontItem.value === tableSize }"
                  @click="fontSizeChange(fontItem)"
                >
                  {{ fontItem.label }}
                </li>
              </ul>
            </div>
          </el-popover>

          <el-popover
            v-if="!hideOperButton"
            :ref="`dynamic-column-${scope.$index}`"
            placement="bottom"
            trigger="click"
            :visible-arrow="false"
            popper-class="scim-common-theme scim-common-theme-green"
            class="cursor-p self-popover"
          >
            <span slot="reference" title="列设置">
              <svg-icon icon-class="set" />
            </span>
            <!-- 列设置 -->
            <columns-setting
              :columns="tableColumns"
              :code="code"
              :version="version"
              :customSort="tableSortCustom"
              @resetColumns="(...args) => handleResetColumns(`dynamic-column-${scope.$index}`, ...args)"
              @changeColumns="handleChangeColumns"
              @applyColumns="(...args) => handleApplyColumns(`dynamic-column-${scope.$index}`, ...args)"
              v-on="$listeners"
            />
          </el-popover>
        </template>
        <template slot-scope="scope">
          <slot name="operation" :scope="scope"></slot>
          <el-popover
            v-if="$scopedSlots.operationMore"
            placement="bottom"
            trigger="hover"
            popper-class="scim-common-theme scim-common-theme-green"
            class="cursor-p self-popover hover_popover"
            :visible-arrow="false"
          >
            <span slot="reference">
              <i class="el-icon-more" style="transform: rotate(90deg);" />
            </span>
            <div class="self-popover-items">
              <slot name="operationMore" :scope="scope"></slot>
            </div>
          </el-popover>
        </template>
      </el-table-column>

      <template slot="empty" class="empty">
        <empty v-show="!data || !data.length" />
      </template>
    </el-table>
    <!-- 表格分页 -->
    <pagination
      v-if="!noPagination"
      ref="pagination"
      :page-info="pagination"
      v-on="$listeners"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />
    <!-- /表格分页 -->
  </div>
</template>

<script>
import ExportJsonExcel from "js-export-excel";
import tableApi from "@/api/general-table";
import roleApi from "@/api/role/role";
import Pagination from "@/components/common/Pagination";
import BatchDelete from "@/components/common/batch-delete";
import Empty from "@/components/private/empty";
import ColumnsSetting from "./ColumnsSetting";
import SearchForm from "./SearchForm";
import { selectionMixins } from "./table/selection";
import { elementMixins } from "./table/elementMixins";
// import inputSearch from "@/components/common/input-search";
import { densityOptions, fontSizeOptions } from "./const/table";
import { $upperFirst } from "@/utils/components/helper";

export default {
  name: "GeneralTable",
  components: { Pagination, BatchDelete, Empty, ColumnsSetting, SearchForm },
  mixins: [selectionMixins, elementMixins],
  inheritAttrs: true,
  props: {
    multi: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: "small"
    },
    resourcePath: {
      type: String,
      default: ""
    },
    formOptions: {
      type: Object,
      default: () => {}
    },
    searchMode: {
      type: String,
      default: "basic"
    },
    searchBasic: {
      type: Object,
      default: () => ({ value: "", placeholder: "" })
    },
    searchAdvanced: {
      type: Object,
      default: () => ({})
    },
    code: {
      type: String,
      default: ""
    },
    version: {
      type: Number,
      default: 1
    },
    title: {
      type: String,
      default: null
    },
    hideOperButton: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: null
    },
    columns: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    tableFetchApi: {
      type: Function,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    rowKey: {
      type: String,
      default: "id"
    },
    checkbox: {
      type: Boolean,
      default: false
    },
    order: {
      type: Boolean,
      default: true
    },
    operable: {
      type: Boolean,
      default: true
    },
    operableWidth: {
      type: Number,
      default: 150
    },
    exportType: {
      type: String,
      default: ""
    },
    pagination: {
      type: Object,
      default: () => ({ pageNum: 1, pageSize: 10, total: 0 })
    },
    noPagination: Boolean,
    excelFileName: {
      type: String,
      default: "表格导出"
    },
    defaultCheckedKeys: {
      type: Array,
      default: () => []
    },
    showAllCurrent: {
      type: Boolean,
      default: false
    },
    contentTooltip: {
      type: Boolean,
      default: true
    },
    autoHeight: {
      type: Boolean,
      default: false
    },
    tableSortCustom: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      tableHeight: undefined,
      tableColumns: [], // 列数据
      tableData: [], // 表格数据
      pageNum: this.pagination.pageNum,
      pageSize: this.pagination.pageSize,
      total: this.pagination.total,
      searchValue: "",
      densityOptions, // 行高密度选项
      tableDensity: "small", // 行高密度选中
      densityVisible: false,
      fontSizeOptions, // 字体大小选项
      tableSize: this.size,
      operateVisible: false, // 列设置弹出框
      multipleSelection: [], // 选中的行
      tableFilters: {} // table本身的过滤条件
    };
  },
  computed: {
    // 展示的列，去除隐藏的列
    viewColumns() {
      return this.tableColumns.filter(item => !item.hidden);
    },
    tableParams() {
      return {
        page: this.pageNum,
        size: this.pageSize,
        search: this.searchValue
      };
    },
    skeletonParams() {
      return {
        loading: this.loading
      };
    }
  },

  watch: {
    defaultCheckedKeys: {
      handler(val, oldVal) {
        this.multipleSelection = JSON.parse(JSON.stringify(val || []));
      },
      deep: true,
      immediate: true
    },
    reserveSelection(newVal, oldVal) {
      if (!newVal) { this.multipleSelection = []; }
    },
    data(newVal) {
      this.tableData = newVal;
      // 数据变化之后，重新doLayout
      this.$nextTick(() => {
        this.$refs.generalTable.doLayout();
      });
    },
    autoHeight: {
      immediate: true,
      handler(newValue) {
        // 使用自适应高度
        const { height, maxHeight } = this.$attrs;

        // 使用外部传输的
        if (height || maxHeight) {
          this.tableHeight = height || maxHeight;
          return;
        }

        // 使用自适应高度
        if (newValue) {
          this.calculateTableHeight();
          return;
        }

        // 使用默认的页高
        let count = 10;
        if (this.tableData && this.tableData.length) {
          count = this.tableData.length;
        }

        const h = count * 40 + 48 + 6; // 48表头、2表头分割线，6边框;
        this.tableHeight = h;
      }
    }
  },
  created() {
    // 初始化table的属性和高度
    this.initTable();
  },
  methods: {
    $upperFirst,
    tableFetchData(options) {
      this.tableFetchApi(this.tableParams).then(res => {
        const { records, total } = res.data;
        this.tableData = records;
        this.total = Number(total);
      });
    },
    renderSelectionHeader(h) {
      console.info("renderHeader");
      return h("div", {}, "选择");
    },
    /**
     * @description: 页面数据初始化
     */
    initTable() {
      // 优先使用服务器保存的列信息
      Promise.all([this.getColumns(), this.getResources()]).then(([columns, resources]) => {
        this.initTableColumns(columns, resources);
      });

      if (this.tableFetchApi) {
        this.tableFetchData();
      } else {
        this.tableData = this.data;
      }

      /*
       * this.saveColumns();
       * this.saveFormQuery();
       */
    },
    // 计算表格高度
    calculateTableHeight() {
      this.$nextTick(() => {
        const tableEl = this.$refs.generalTable;
        const paginationEl = this.$refs.pagination;
        if (tableEl) {
          const { top } = tableEl.$el.getBoundingClientRect();
          const { height } = paginationEl ? paginationEl.$el.getBoundingClientRect() : { height: 10 };
          // 获取浏览器高度
          const h = document.documentElement.clientHeight || document.body.clientHeight;
          this.tableHeight = `${h - top - height}px`;
        }
      });
    },

    /**
     * @description: 设置列数据
     * @param {*} columns 列数据
     */
    initTableColumns(columns, resources) {
      if (!Array.isArray(columns) || !columns.length) { return; }

      // 过滤资源权限
      let tableColumnsTemp = columns;
      if (Array.isArray(resources) && resources.length) {
        tableColumnsTemp = columns.filter(item => this.visibleRes(item.key || item.name, resources)
        );
      }
      // 处理兼容数据
      this.tableColumns = tableColumnsTemp
        .map(col => {
          // 固定列兼容不同的fixed值
          if (col.fixed === "true") {
            col.fixed = true;
          } else if (col.fixed === "false") {
            col.fixed = false;
          } else {
            col.fixed = col.fixed || false;
          }
          return {
            ...col,
            label: col.label || col.displayName,
            key: col.key || col.name,
            hidden: col.hidden || false, // 列显隐
            auth: !col.auth && typeof col.auth !== "boolean" ? true : col.auth, // 列权限
            sortable: col.sortable || false // 列排序
          };
        })
        .filter(col => col.auth && (col.key || col.name));
    },

    /**
     * @description: 列设置操作框关闭事件
     */
    operatePopoverClose(popoverKey) {
      this.operateVisible = false;
      if (this.$refs[popoverKey] && this.$refs[popoverKey].doClose) {
        this.$refs[popoverKey].doClose();
        this.$refs[popoverKey].showPopper = false;
      }
    },
    // 奇偶行数颜色
    tableRowClassName({ row, rowIndex }) {
      // 偶数
      if (rowIndex % 2 === 0) {
        return "stripe_row";
      }

      return "default_row";
    },

    /**
     * @description: 表头数据重置事件
     */
    handleResetColumns(popoverKey = "operatePopover") {
      // 根据传入的值重新获取tableColumns
      this.initTableColumns(this.columns);
      this.operatePopoverClose(popoverKey);
    },

    /**
     * @description: 表头数据应用保存事件
     */
    handleChangeColumns(list) {
      // 根据传入的值重新获取tableColumns
      this.initTableColumns(list);
    },
    handleApplyColumns(popoverKey = "operatePopover", list) {
      // 根据传入的值重新获取tableColumns
      this.initTableColumns(list);
      this.operatePopoverClose(popoverKey);
    },
    // 优先使用服务器保存的列信息
    async getColumns() {
      if (this.code && !/^\s*$/gi.test(this.code)) {
        const { data = {}} = await tableApi.getFormByCode(this.code);
        const { formVersion, columnShow = [] } = data;
        if (formVersion >= this.version && columnShow.length) {
          return Promise.resolve(columnShow);
        }
        return Promise.resolve(this.columns);
      }
      return Promise.resolve(this.columns);
    },
    // 获取服务器端保存的资源信息
    async getResources() {
      if (this.resourcePath) {
        const { data } = await roleApi.getPermission({ resourcePath: this.resourcePath });
        return Promise.resolve(data);
      }
      return Promise.resolve([]);
    },

    /**
     * @description: 保存用户表单查询条件配置
     */
    saveFormQuery() {
      tableApi
        .saveFormQuery(this.code, [
          {
            displayName: "xx1",
            hidden: false,
            name: "name1",
            value: "value1"
          },
          {
            displayName: "xx2",
            hidden: true,
            name: "name2",
            value: "value2"
          }
        ])
        .then(res => {
          // eslint-disable-next-line no-empty
          if (res && res.data) {
          }
        });
    },

    /**
     * @description: 计算列序号
     * @param {object} index
     * @return {number} 序号
     */
    tableIndex(index) {
      return (this.pagination.pageNum - 1) * this.pagination.pageSize + index + 1;
    },

    /**
     * @description: 列拖拽改变宽度事件
     * @param {number} newWidth 拖拽后的宽度
     * @param {number} oldWidth 之前的宽度
     * @param {object} column 当前列
     * @param {object} event event事件对象
     */
    handleHeaderDragend(newWidth, oldWidth, column, event) {},

    /**
     * @description: 分页显示条数改变事件
     * @param {*} data 当前显示条数
     */
    handleSizeChange(data) {
      this.pageSize = data.pageSize;
      this.tableFetchApi && this.tableFetchData();
      this.$emit("handleSizeChange", data);
    },

    /**
     * @description: 翻页事件
     * @param {*} data 当前页数
     */
    handleCurrentChange(data) {
      this.pageNum = data.pageNum;
      this.tableFetchApi && this.tableFetchData();
      this.$emit("handleCurrentChange", data);
    },

    /**
     * @description: 表头过滤事件
     * @param {string} value 当前值
     * @param {Object} row 行信息
     * @param {Object} column 列信息
     * @return {Boolean} 是否符合过滤条件
     */
    filterHandler(value, row, column) {
      const property = column["property"];
      return row[property] === value;
    },

    // 表格过滤条件
    handleChangeFilter(filter) {
      const [key] = Object.keys(filter);
      this.$set(this.tableFilters, key, filter[key]);
      this.$emit("tableFilterChange", this.tableFilters);
    },

    /**
     * @description: 表格数据刷新事件
     */
    handleRefresh() {
      this.$emit("refresh");
    },

    /**
     * @description: 表格行高改变事件
     * @param {*} 当前选择值
     */
    densityChange(data) {
      this.tableDensity = data.value;
      this.densityVisible = false;
      // 数据变化之后，重新doLayout
      this.$nextTick(() => {
        this.$refs.generalTable.doLayout();
      });
    },

    /**
     * @description: 表格字体大小改变事件
     * @param {*} 当前选择值
     */
    fontSizeChange(data) {
      this.tableSize = data.value;
      // 数据变化之后，重新doLayout
      this.$nextTick(() => {
        this.$refs.generalTable.doLayout();
      });
    },
    // 导出当前页数据
    exportCurrentData() {
      const option = {
        fileName: this.excelFileName,
        datas: [
          {
            sheetData: this.data,
            sheetName: "sheet",
            sheetFilter: this.tableColumns.map(col => col.label),
            sheetHeader: this.tableColumns.map(col => col.key)
          }
        ]
      };
      const toExcel = new ExportJsonExcel(option);
      toExcel.saveExcel && toExcel.saveExcel();
    },
    // 导出数据
    exportAllData() {
      this.$emit("exportAllData");
    },
    // 搜索
    handleSearch(searchValue) {
      this.searchValue = searchValue;
    }
  }
};
</script>

<style lang="scss">
.ellipsisWarp {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.general_table_wrap {
  .rc_table {
    font-size: 16px;
    thead {
      color: #21252e;
      height: 48px;
      th,th.is-leaf:hover {
        border-right: 1px solid #d4d9e2;
      }
    }

    .el-table__cell {
      .cell {
        .el-icon-arrow-down {
          display: none;
        }
      }
    }

    .el-table__row {
      .cell {
        display: flex;
        box-sizing: border-box;
        align-items: center;
      }
    }

    .stripe_row {
      background: #f5f7fa;
    }

    .default_row {
      background: #fff;
    }
  }

  .el-table--small {
    font-size: 14px;
  }

  .el-table--mini {
    font-size: 12px;
  }

  .rc_table_density--medium {
    td {
      height: 44px;
    }
  }

  .rc_table_density--mini {
    td {
      height: 36px;
    }
  }
}

.self-popover {
  margin-left: 5px;
}

.fontsize_popover,
.lineHeight_popover {
  width: 128px;

  li {
    padding: 10px;
    cursor: pointer;

    &:hover,
    &.active {
      color: $scim-color-primary;
      background: $scim-color-table-block;
    }
  }
}

.operate_more {
  display: inline-block;
}
</style>
